// @givmo/board — the board roster: who serves, in what seat, since when.
//
// The two things this screen must get right, both from the backend's model rather than from UI taste:
//
//   * A SEAT IS NOT AN ACCOUNT, BUT A MEMBER JOINS WITH ONE (CBG-SEC2, D-CBG-16/17). The officer
//     records a director from a name and an address; inviting them sends an invitation, and the
//     member joins the seat into their own Givmo account — by an address that matches, or by a code
//     sent to the seat's address. The roster says which stage each seat is at, in one plain word:
//     not invited, invited, joined. An officer never sets the connection, and a seat that never joins
//     still works: the officer records signatures and replies on the member's behalf.
//   * REMOVING IS A STATUS FLIP, NOT A DELETE. A former director's acts must keep resolving to a named
//     seat, so the row stays and moves to "Former". The confirm copy says exactly that, because a
//     volunteer reading "Remove" reasonably fears it erases history.
//
// Roster COMPOSITION is Owner-only (`board_member:manage`) while inviting an existing member is
// Member+Owner (`board_member:grant_access`) — the split that keeps invitations from being a path into
// changing who is on the board.
//
// EACH PERSON'S TWO FACTS (CBG-YR3-ROLES layer 1, D-CBG-21) have a column each: Director — the word,
// or "—" — and Titles — a list, or "—" — in plain words rather than badges (colour only where someone
// must act), with the term in its own column. The count line says how many directors serve beside how
// many people do. On a phone each person's facts read as one line, `capacityLine`'s.
//
// PEOPLE WHO AREN'T ON THE BOARD (CBG-YR6-OUTSIDE, frame o2) serve on a committee through a seat
// scoped to its committees. They are listed in their own group beneath the board's table, and never
// in its count: their person, their committees and where they are with joining — no Director, Titles
// or Term, which such a seat has none of. The group shows only while there is someone to show; such a
// seat is added from a committee's "Add a member", never here.
//
// CBG-AI5-PROPOSE (frames r1–r3; backend `board-ai-proposals.md` §6.1): the seats the organization's
// connected AI proposed wait in their own card ABOVE the table, never in it or its count (a proposal is
// not a seat, D-CBG-34 reading 1). "Review and add" opens the roster's own "Add a board member" filled
// from the proposal, whose save is the guarded add carrying the proposal's id; "Discard" asks once.
// Shown only to someone holding `board_member:manage`, the add's own permission.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '../../board-routing';
import { BOARD_PAGES } from '../../board-routes';

import { isGivmoError, type OrgBoardMemberOut } from '@givmo/api-client';
import {
  Badge,
  Button,
  DataTable,
  Dialog,
  EmptyState,
  ErrorMessage,
  Icon,
  RowActions,
  toast,
  Tabs,
} from '@givmo/design-system';
import { Users } from 'lucide-react';
import { PageShell } from '../../components/page-shell';
import { useBoardHost } from '../../board-host';
import { hasBoardPermission } from '../../board-host';
import { InvitationDialog } from './invitation-dialog';
import { RemoveSeatDialog } from './remove-seat-dialog';
import { SeatFormDialog, type SeatFormMode } from './seat-form-dialog';
import { HistoricalSeatDialog } from './historical-seat-dialog';
import {
  connectionLabel,
  extractBoardMembers,
  inviteActionLabel,
  isCommitteeOnlySeat,
  isHistoricalSeat,
  seatCommittees,
  seatConnection,
  serviceLine,
  sortSeats,
  statusLabel,
  statusTone,
} from './roster-decode';
import {
  capacityLine,
  isDirector,
  keptTogether,
  servingDirectors,
  titlesCell,
} from './officer-roles';
import { officerRolesWords as w } from './officer-roles-words';
import { useBoardOperations } from '../../board-runtime';
import { displayDate, todayAsDateInput } from '../../lib/display';
import { currentTerm, heldOverEnd } from './terms-decode';
import { termsWords as tw } from './terms-words';
// CBG-YR4-START (frame g2): the board's own getting-started list, as a second part.
import { BoardListPart } from '../onboarding/board-list-part';
import { onboardingWords as gs } from '../onboarding/onboarding-words';
// CBG-YR6-OUTSIDE (frame o2): the second group's words.
import { committeeMeetingsWords as cm } from '../committees/committee-meetings-words';
import { committeeOnlyWords as co } from '../committees/committee-only-words';
// CBG-AI5-PROPOSE: the proposed members, their reads and their words.
import { ProposedMembersCard } from '../proposals/proposed-members-card';
import type { MemberProposal } from '../proposals/proposal-decode';
import { useMemberProposals, useSettleProposals } from '../proposals/proposal-queries';
import { proposalWords as pw } from '../proposals/proposal-words';
import { servedSentence } from '../returns/return-queries';

/**
 * The Term cell (CBG-YR3-TERMS, frame t3): a serving seat's current term — its end, or the approved
 * holdover words once that end has passed ("Ended Mar 31, 2026 – still serving"), and its class. A
 * former seat, or one with no end recorded, keeps the service line it always had.
 */
export function rosterTermCell(seat: OrgBoardMemberOut, today: string): string | null {
  const term = currentTerm(seat);
  if (term === null || seat.status !== 'active') return serviceLine(seat);
  const heldOver = heldOverEnd(term, seat, today);
  const end =
    heldOver !== null
      ? keptTogether(tw.rosterEnded(displayDate(heldOver)))
      : term.ends_on
        ? tw.rosterEnds(displayDate(term.ends_on))
        : null;
  const parts = [end, term.class_label ?? null].filter((part): part is string => !!part);
  return parts.length > 0 ? parts.join(' · ') : serviceLine(seat);
}

/** The person: their name, opening their page, and their address (both tables). */
const MEMBER_COLUMN = {
  id: 'name',
  header: 'Member',
  // Stacked: the name heads the block, its lane stopping short of the menu parked at the
  // block's corner (compliance's documents table, the console's pattern for a row menu).
  stackedClassName: 'max-sm:pr-36',
  cell: (seat: OrgBoardMemberOut) => (
    <span className="flex min-w-0 flex-col">
      {/* A FORMER seat links too. Its acts are still on the record and still resolve here, which
        is the whole reason removal keeps the row. A person's name WRAPS at every width and is
        never cut short; only the address, on its own line beneath, may clip. */}
      <Link
        to="/roster/$memberId"
        params={{ memberId: seat.id }}
        className="whitespace-normal font-w600 text-text-strong outline-none transition-colors wrap-anywhere hover:text-brand-700 focus-visible:underline"
        data-testid={`roster-open-${seat.id}`}
      >
        {seat.display_name}
      </Link>
      <span className="truncate text-s12 text-text-tertiary">{seat.email_of_record}</span>
    </span>
  ),
  truncate: true,
};

/** The seat's status and, while it serves, where they are with joining (both tables). */
const STATUS_COLUMN = {
  id: 'status',
  header: 'Status',
  // Stacked: the badge and the connection word on one line under the seat.
  stacked: 'line' as const,
  cell: (seat: OrgBoardMemberOut) => (
    <span className="flex min-w-0 flex-col items-start gap-4 max-sm:flex-row max-sm:flex-wrap max-sm:items-center max-sm:gap-x-8">
      <span className="flex items-center gap-8">
        <Badge tone={statusTone(seat.status)}>{statusLabel(seat.status)}</Badge>
        {isHistoricalSeat(seat) ? (
          // The organization's own account of a past seat, versus one this module watched end. The
          // dates mean different things, so the distinction is shown rather than flattened.
          <Badge tone="neutral">From your records</Badge>
        ) : null}
      </span>
      {seat.status === 'active' ? (
        // Where the member is with joining, as a plain word on its own quiet line (the seat
        // cell's pattern): a fact, not a control.
        <span
          className="truncate text-s12 text-text-tertiary"
          data-testid={`roster-connection-${seat.id}`}
        >
          {connectionLabel(seatConnection(seat))}
        </span>
      ) : null}
    </span>
  ),
  width: '200px',
};

const selectSeats = (data: unknown): OrgBoardMemberOut[] => sortSeats(extractBoardMembers(data));

export function RosterPage() {
  // CBG-YR4-START: which part the address names (`?tab=getting-started`), else Members.
  const search = useSearch();
  const navigate = useNavigate();
  const tab = search.tab === 'getting-started' ? 'getting-started' : 'members';
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  const { permissions, orgRef, isReady, userId } = useBoardHost();
  const canManageRoster = hasBoardPermission(permissions, 'board_member:manage');
  // Inviting a member is its own string, `board_member:grant_access` — a Member one, so neither
  // implies the other.
  const canInvite = hasBoardPermission(permissions, 'board_member:grant_access');

  const [seatForm, setSeatForm] = useState<{
    mode: SeatFormMode;
    seat: OrgBoardMemberOut | null;
  } | null>(null);
  const [inviting, setInviting] = useState<OrgBoardMemberOut | null>(null);
  const [removing, setRemoving] = useState<OrgBoardMemberOut | null>(null);
  const [recordingHistorical, setRecordingHistorical] = useState(false);
  // CBG-AI5-PROPOSE: what the connected AI proposed (none for a reader who can't add a member), and
  // the proposal being reviewed into the add or discarded.
  const proposals = useMemberProposals();
  const settleProposals = useSettleProposals();
  const [reviewing, setReviewing] = useState<MemberProposal | null>(null);
  const [discarding, setDiscarding] = useState<MemberProposal | null>(null);

  // `include_removed`: the roster is everyone who serves AND everyone who has, so its former rows and
  // count are real. The read answers serving seats alone without it (its default).
  const seatsOptions = operations.officerListBoardMembersOptions({
    path: { org_ref: orgRef ?? '' },
    query: { include_removed: true },
  });
  const seatsQuery = useQuery({
    ...seatsOptions,
    enabled: isReady && orgRef !== null,
    select: selectSeats,
  });

  const invalidateSeats = () => queryClient.invalidateQueries({ queryKey: seatsOptions.queryKey });

  const removeSeat = useMutation({
    // The reason is REQUIRED by the backend, deliberately: ending a seat ends someone's ability to act
    // for the board, so it is never an unexplained flag flip. The dialog collects it.
    mutationFn: async ({ seat, reason }: { seat: OrgBoardMemberOut; reason: string }) => {
      await operations.officerRemoveBoardMember({
        path: { org_ref: orgRef ?? '', member_id: seat.id },
        body: { reason },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      setRemoving(null);
      await invalidateSeats();
      toast.success('This seat is now recorded as former.');
    },
    onError: (caught) => {
      toast.error(
        isGivmoError(caught) ? caught.message : 'We couldn’t update that seat. Please try again.',
      );
    },
  });

  // The board's seats in its table, and only they in its count; the seats of people who aren't on
  // the board in their own group beneath it (CBG-YR6-OUTSIDE).
  const allSeats = seatsQuery.data;
  const seats = useMemo(
    () => (allSeats ?? []).filter((seat) => !isCommitteeOnlySeat(seat)),
    [allSeats],
  );
  const outsideSeats = useMemo(() => (allSeats ?? []).filter(isCommitteeOnlySeat), [allSeats]);
  const activeCount = useMemo(() => seats.filter((s) => s.status === 'active').length, [seats]);
  const directorCount = useMemo(() => servingDirectors(seats).length, [seats]);
  // The reader's calendar day, for the Term column's holdover words (CBG-YR3-TERMS).
  const today = todayAsDateInput();

  // Memoized so rows skip re-render on unrelated parent state. The deps are exactly what the
  // cells close over, so a real change still rebuilds them.
  const columns = useMemo(
    () => [
      MEMBER_COLUMN,
      {
        id: 'director',
        header: w.columnDirector,
        // Stacked: one line under the name — the person's facts in the one helper's words — while the
        // Titles column steps aside (this line carries the titles).
        stacked: 'line' as const,
        cell: (seat: OrgBoardMemberOut) => (
          <>
            <span
              className="hidden max-sm:inline max-sm:whitespace-normal"
              data-testid={`roster-capacity-${seat.id}`}
            >
              {capacityLine(seat)}
            </span>
            <span className="max-sm:hidden" data-testid={`roster-director-${seat.id}`}>
              {isDirector(seat) ? w.directorCellYes : w.directorCellNo}
            </span>
          </>
        ),
        width: '13%',
      },
      {
        id: 'titles',
        header: w.columnTitles,
        // On a phone the Director column's line already carries the titles (`capacityLine`).
        stacked: 'omit' as const,
        cell: (seat: OrgBoardMemberOut) => {
          const titles = titlesCell(seat);
          return titles === null ? (
            <span className="text-text-tertiary">—</span>
          ) : (
            <span className="truncate" data-testid={`roster-titles-${seat.id}`}>
              {titles}
            </span>
          );
        },
        width: '18%',
      },
      {
        id: 'term',
        header: w.columnTerm,
        stacked: 'line' as const,
        cell: (seat: OrgBoardMemberOut) =>
          rosterTermCell(seat, today) !== null ? (
            <span
              className="truncate text-text-secondary max-sm:whitespace-normal max-sm:text-s13 max-sm:text-text-tertiary"
              data-testid={`roster-term-${seat.id}`}
            >
              {rosterTermCell(seat, today)}
            </span>
          ) : (
            <span className="text-text-tertiary max-sm:hidden">—</span>
          ),
        width: '17%',
      },
      STATUS_COLUMN,
      {
        id: 'actions',
        header: '',
        align: 'right' as const,
        width: '64px',
        // Stacked: the menu parks at the block's top-right corner (the name's lane stops short of it).
        stacked: 'line' as const,
        cell: (seat: OrgBoardMemberOut) => {
          // Only the acts the person holds: an invitation is `board_member:grant_access`, changing the
          // seat is `board_member:manage`. A member who has joined needs no invitation.
          const actions = [
            ...(canInvite && seatConnection(seat) !== 'joined'
              ? [
                  {
                    label: inviteActionLabel(seatConnection(seat)),
                    testId: `roster-invite-${seat.id}`,
                    onSelect: () => setInviting(seat),
                  },
                ]
              : []),
            ...(canManageRoster
              ? [
                  {
                    label: 'Edit this seat',
                    testId: `roster-edit-${seat.id}`,
                    onSelect: () => setSeatForm({ mode: 'edit', seat }),
                  },
                  {
                    label: 'Record as former',
                    testId: `roster-remove-${seat.id}`,
                    destructive: true,
                    onSelect: () => setRemoving(seat),
                  },
                ]
              : []),
          ];
          return seat.status !== 'active' || actions.length === 0 ? null : (
            <div className="max-sm:absolute max-sm:right-8 max-sm:top-8">
              <RowActions
                testId={`roster-actions-${seat.id}`}
                label={`Actions for ${seat.display_name}`}
                actions={actions}
              />
            </div>
          );
        },
      },
    ],
    [canManageRoster, canInvite, today],
  );

  // CBG-YR6-OUTSIDE (frame o2): the group's columns — the person, their committees (each "(chair)"
  // where it applies) and where they are with joining. Committees takes the width the board's
  // Director, Titles and Term take, so Status lines up with the table above. Removed before
  // finishing: those three columns, which such a seat has none of.
  const outsideColumns = useMemo(
    () => [
      MEMBER_COLUMN,
      {
        id: 'committees',
        header: co.columnCommittees,
        stacked: 'line' as const,
        cell: (seat: OrgBoardMemberOut) => (
          <span className="whitespace-normal" data-testid={`roster-committees-${seat.id}`}>
            {seatCommittees(seat)
              .map((row) => keptTogether(cm.committeeWithRole(row.name, row.role === 'chair')))
              .join(' · ')}
          </span>
        ),
        width: '48%',
      },
      STATUS_COLUMN,
      {
        id: 'actions',
        header: '',
        align: 'right' as const,
        width: '64px',
        stacked: 'line' as const,
        cell: (seat: OrgBoardMemberOut) => {
          const connection = seatConnection(seat);
          const names = seatCommittees(seat).map((row) => row.name);
          // A seat whose every membership has ended opens nothing, so it isn't offered an invitation.
          const actions = [
            ...(canInvite && connection !== 'joined' && names.length > 0
              ? [
                  {
                    label:
                      connection === 'invited' ? inviteActionLabel(connection) : co.inviteTo(names),
                    testId: `roster-invite-${seat.id}`,
                    onSelect: () => setInviting(seat),
                  },
                ]
              : []),
            ...(canManageRoster
              ? [
                  {
                    label: 'Edit this seat',
                    testId: `roster-edit-${seat.id}`,
                    onSelect: () => setSeatForm({ mode: 'edit', seat }),
                  },
                  {
                    label: 'Record as former',
                    testId: `roster-remove-${seat.id}`,
                    destructive: true,
                    onSelect: () => setRemoving(seat),
                  },
                ]
              : []),
          ];
          return seat.status !== 'active' || actions.length === 0 ? null : (
            <div className="max-sm:absolute max-sm:right-8 max-sm:top-8">
              <RowActions
                testId={`roster-actions-${seat.id}`}
                label={`Actions for ${seat.display_name}`}
                actions={actions}
              />
            </div>
          );
        },
      },
    ],
    [canManageRoster, canInvite],
  );

  return (
    <PageShell
      title="Board members"
      description="Everyone who serves on this board, and everyone who has. Members take part with their own Givmo account, or you can record their signatures and replies for them."
      actions={
        // Changing who is on the board is `board_member:manage`: a reader is offered neither act.
        canManageRoster && tab === 'members' ? (
          <div className="flex flex-wrap gap-10">
            <Button
              appearance="saas"
              tone="secondary"
              disabled={!isReady}
              onClick={() => setRecordingHistorical(true)}
              testId="roster-add-historical"
            >
              Record someone who served before
            </Button>
            <Button
              appearance="saas"
              tone="primary"
              disabled={!isReady}
              onClick={() => setSeatForm({ mode: 'create', seat: null })}
              testId="roster-add-seat"
            >
              Add a member
            </Button>
          </div>
        ) : undefined
      }
      testId="page-roster"
    >
      <Tabs.Root
        appearance="saas"
        value={tab}
        onValueChange={(value) =>
          void navigate({
            to: BOARD_PAGES.members,
            search: value === 'getting-started' ? { tab: 'getting-started' } : {},
            replace: true,
          })
        }
        testId="roster-tabs"
      >
        <Tabs.List aria-label="Board members">
          <Tabs.Tab value="members" testId="roster-tab-members">
            {gs.membersTab}
          </Tabs.Tab>
          <Tabs.Tab value="getting-started" testId="roster-tab-getting-started">
            {gs.listTab}
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="getting-started" focusable={false}>
          <BoardListPart />
        </Tabs.Panel>
        <Tabs.Panel value="members" focusable={false}>
          {/* CBG-AI5-PROPOSE (frame r1): what the connected AI proposed, above the table. */}
          {proposals.length > 0 ? (
            <div className="mb-24">
              <ProposedMembersCard
                proposals={proposals}
                viewerUserId={userId}
                onReview={setReviewing}
                onDiscard={setDiscarding}
              />
            </div>
          ) : null}
          <DataTable<OrgBoardMemberOut>
            testId="roster-table"
            // Below `sm` each seat stacks into a block (the design system's cure for a table squeezed
            // onto a phone): the name heads it and wraps, the seat and the status each take a line, and
            // the menu parks at the corner. From 640px up the table is unchanged.
            stackAt="sm"
            columns={columns}
            rows={seats}
            rowKey={(seat) => seat.id}
            rowTestId={(seat) => `roster-row-${seat.id}`}
            density="comfortable"
            loading={seatsQuery.isPending && isReady}
            loadingTestId="roster-loading"
            error={seatsQuery.isError ? 'We couldn’t load this board’s members.' : undefined}
            errorTestId="roster-error"
            retryTestId="roster-error-retry"
            onRetry={() => void seatsQuery.refetch()}
            emptyState={
              <EmptyState
                testId="roster-empty"
                icon={<Icon icon={Users} size={20} aria-hidden />}
                title="No members recorded yet"
                description={
                  canManageRoster
                    ? 'Add the people who serve on this board. You only need a name and an email address.'
                    : undefined
                }
                action={
                  canManageRoster ? (
                    <Button
                      appearance="saas"
                      tone="primary"
                      onClick={() => setSeatForm({ mode: 'create', seat: null })}
                      testId="roster-empty-add"
                    >
                      Add a member
                    </Button>
                  ) : undefined
                }
              />
            }
            footer={
              seats.length > 0 ? (
                <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                  {w.count(directorCount, activeCount, seats.length - activeCount)}
                </span>
              ) : undefined
            }
          />
          {outsideSeats.length > 0 ? (
            <section className="mt-28 flex flex-col gap-12" data-testid="roster-outside">
              <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
                {co.groupTitle}
              </h2>
              <DataTable<OrgBoardMemberOut>
                testId="roster-outside-table"
                stackAt="sm"
                columns={outsideColumns}
                rows={outsideSeats}
                rowKey={(seat) => seat.id}
                rowTestId={(seat) => `roster-row-${seat.id}`}
                density="comfortable"
              />
            </section>
          ) : null}
        </Tabs.Panel>
      </Tabs.Root>

      {/* CBG-UX9 (frames s1–s4): a holder of the invitation's string is asked, once the add has
          saved, whether to invite the new member; the row behind says where the seat is. */}
      {seatForm !== null && orgRef !== null ? (
        <SeatFormDialog
          orgRef={orgRef}
          mode={seatForm.mode}
          seat={seatForm.seat}
          offerInvitation={canInvite}
          onChanged={() => void invalidateSeats()}
          onClose={() => setSeatForm(null)}
          onSaved={() => {
            setSeatForm(null);
            void invalidateSeats();
          }}
        />
      ) : null}

      {/* CBG-AI5-PROPOSE (frame r2): the roster's own add, filled from the proposal — and the same
          question after it (CBG-UX9, frame p1). */}
      {reviewing !== null && orgRef !== null ? (
        <SeatFormDialog
          orgRef={orgRef}
          mode="create"
          seat={null}
          proposal={reviewing}
          offerInvitation={canInvite}
          onChanged={() => {
            void invalidateSeats();
            void settleProposals();
          }}
          onClose={() => {
            setReviewing(null);
            // A refusal saying it was decided elsewhere is answered by reading the list again.
            void settleProposals();
          }}
          onSaved={() => {
            setReviewing(null);
            void invalidateSeats();
            void settleProposals();
          }}
        />
      ) : null}

      {/* CBG-AI5-PROPOSE (frame r3): discarding a proposal, asked once. */}
      {discarding !== null && orgRef !== null ? (
        <DiscardProposalDialog
          orgRef={orgRef}
          proposal={discarding}
          onClose={() => setDiscarding(null)}
          onDiscarded={() => {
            setDiscarding(null);
            toast.success(pw.memberDiscarded);
            void settleProposals();
          }}
        />
      ) : null}

      {recordingHistorical && orgRef !== null ? (
        <HistoricalSeatDialog
          orgRef={orgRef}
          onClose={() => setRecordingHistorical(false)}
          onSaved={() => {
            setRecordingHistorical(false);
            void invalidateSeats();
          }}
        />
      ) : null}

      {inviting !== null && orgRef !== null ? (
        <InvitationDialog
          orgRef={orgRef}
          seat={inviting}
          committees={
            isCommitteeOnlySeat(inviting) ? seatCommittees(inviting).map((row) => row.name) : null
          }
          onClose={() => {
            setInviting(null);
            void invalidateSeats();
          }}
        />
      ) : null}

      {removing !== null ? (
        <RemoveSeatDialog
          seat={removing}
          pending={removeSeat.isPending}
          onCancel={() => setRemoving(null)}
          onConfirm={(reason) => removeSeat.mutate({ seat: removing, reason })}
        />
      ) : null}
    </PageShell>
  );
}

/**
 * Discarding a proposed member (frame r3). The proposal keeps no name or address afterwards
 * (design §5), so it is asked once; a proposal added or discarded meanwhile is refused in the
 * backend's own words (copy 1.9).
 */
function DiscardProposalDialog({
  orgRef,
  proposal,
  onClose,
  onDiscarded,
}: {
  orgRef: string;
  proposal: MemberProposal;
  onClose: () => void;
  onDiscarded: () => void;
}) {
  const operations = useBoardOperations();
  const [error, setError] = useState<string | null>(null);
  const discard = useMutation({
    mutationFn: async () =>
      operations.officerDiscardBoardMemberProposal({
        path: { org_ref: orgRef, proposal_id: proposal.id },
        throwOnError: true,
      }),
    onMutate: () => setError(null),
    onSuccess: onDiscarded,
    onError: (caught) => setError(servedSentence(caught, 'We couldn’t do that. Please try again.')),
  });
  return (
    <Dialog.Root open onOpenChange={(next) => !next && !discard.isPending && onClose()}>
      <Dialog.Content
        aria-label={pw.discardMemberTitle}
        className="flex flex-col gap-16"
        testId="proposed-member-discard-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {pw.discardMemberTitle}
        </Dialog.Title>
        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary wrap-anywhere">
          {pw.discardMemberBody(proposal.display_name ?? '')}
        </p>
        {error !== null ? (
          <ErrorMessage testId="proposed-member-discard-error">{error}</ErrorMessage>
        ) : null}
        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={discard.isPending}
            onClick={onClose}
            testId="proposed-member-discard-keep"
          >
            {pw.keepIt}
          </Button>
          <Button
            appearance="saas"
            tone="danger"
            disabled={discard.isPending}
            onClick={() => discard.mutate()}
            testId="proposed-member-discard-confirm"
          >
            {pw.discard}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
